import {
  useCreateMedComplexMutation,
  useGetGmtListQuery,
  useGetMedComplexModelsListQuery,
  useLazyGetCompanySearchListQuery,
  useUpdateMedComplexMutation,
} from '@/api'
import type { MedComplex } from '@/api/models/medcomplex'
import { Autocomplete, DateTimePicker, Input } from '@/components'
import { useRole } from '@/hooks'
import { formatDate } from '@/utils/helpers'
import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useContext, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Accordion } from '../../components/Accordion'
import { MedComplexContext } from '../../MedComplex'
import { styles } from '../../styles'
import moment from 'moment'

export const GeneralInfo = () => {
  const { errorForms, medcomplex, refetch, typingListener } =
    useContext(MedComplexContext)
  const navigate = useNavigate()
  const [createMedComplex] = useCreateMedComplexMutation()
  const [updateMedComplex] = useUpdateMedComplexMutation()
  // const [payload, setPayload] = useState<Partial<MedComplex>>({
  //   luminosity_lower: 0,
  //   luminosity_upper: 1000,
  // })

  const generalDataForm = useForm()
  const watchGeneralData = generalDataForm.watch([
    'model',
    'bluetooth_serial_number',
    'ownerCompany',
    'clientCompanies',
    'adress',
  ])

  const { data: models } = useGetMedComplexModelsListQuery()
  const modelOptions = models?.map((item) => ({
    label: item.name,
    value: item.id,
  }))

  const { data: gmts } = useGetGmtListQuery()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gmtOptions = gmts?.map((item: any) => ({
    label: `GMT${item.type}${moment(item.value, 'hh:mm').format('h')}`,
    value: item.id,
  }))

  const [getOwners, { data: owners }] = useLazyGetCompanySearchListQuery()
  const ownerAutocomplateHandle = useCallback(
    (value: string) => {
      value && getOwners(value ? value : '')
    },
    [getOwners]
  )
  const ownerOptions = owners?.data?.map((item) => ({
    label: item.short_title ?? '',
    value: item.id ?? '',
  }))

  const [getClients, { data: clients }] = useLazyGetCompanySearchListQuery()
  const clientOptions = [
    ...(generalDataForm.getValues('clientCompanies') ?? []),
    ...(clients?.data?.map((item) => ({
      label: item.short_title ?? '',
      value: item.id ?? '',
    })) ?? []),
  ].filter((company, index, self) => {
    return index === self.findIndex((item) => item.value === company.value)
  })
  const clientAutocomplateHandle = useCallback(
    (value: string) => {
      value && getClients(value ? value : '')
    },
    [getClients]
  )

  const defaults = useMemo(
    async () => ({
      model: medcomplex?.model
        ? {
            label: medcomplex.model?.name,
            value: medcomplex.model?.id,
          }
        : undefined,
      model_type: medcomplex?.model ? medcomplex?.model.type : undefined,
      bluetooth_serial_number: medcomplex?.bluetooth_serial_number ?? undefined,
      date_creation: medcomplex?.date_creation
        ? dayjs(medcomplex?.date_creation)
        : undefined,
      guarantee_period: medcomplex?.guarantee_period
        ? dayjs(medcomplex?.guarantee_period)
        : undefined,
      ownerCompany: medcomplex?.ownerCompany
        ? {
            label: medcomplex.ownerCompany?.short_title,
            value: medcomplex.ownerCompany?.id,
          }
        : undefined,
      clientCompanies: medcomplex?.MedComplexClientCompany
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          medcomplex?.MedComplexClientCompany?.map((item: any) => ({
            label: item?.clientCompany?.short_title,
            value: item?.clientCompanyId,
          }))
        : [],
      adress: medcomplex?.adress,
      gmt: medcomplex?.gmt
        ? {
            label: `GMT${medcomplex?.gmt?.type}${moment(
              medcomplex?.gmt?.value,
              'hh:mm'
            ).format('h')}`,
            value: medcomplex?.gmt?.id,
          }
        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
          gmtOptions?.find((item: any) => item.label === 'GMT+3') ?? undefined,
      appVersion: medcomplex?.appVersion ?? '',
      coreDeviceVersion: medcomplex?.coreDeviceVersion ?? '',
      auxDeviceVersion: medcomplex?.auxDeviceVersion ?? '',
      isSendToNiiat: medcomplex?.isSendToNiiat ? 'Да' : 'Нет',
    }),
    [medcomplex, gmts]
  )

  useMemo(async () => {
    generalDataForm.reset(await defaults)
  }, [defaults])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (value: any) => {
    const dirtyFields = generalDataForm.formState.dirtyFields
    if (Object.keys(dirtyFields).length > 0) {
      const payload: MedComplex = {}

      if (dirtyFields?.model) {
        payload.modelId = value.model.value
      }
      if (dirtyFields?.bluetooth_serial_number) {
        payload.bluetooth_serial_number = String(
          parseInt(value.bluetooth_serial_number)
        )
      }
      if (dirtyFields?.date_creation) {
        payload.date_creation = formatDate(new Date(value.date_creation))
      }
      if (dirtyFields?.guarantee_period) {
        payload.guarantee_period = formatDate(new Date(value.guarantee_period))
      }
      if (dirtyFields?.ownerCompany) {
        payload.ownerCompanyId = value.ownerCompany.value
      }
      if (dirtyFields?.clientCompanies) {
        payload.clientCompaniesIds = value.clientCompanies?.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (item: any) => item.value
        )
      }
      if (dirtyFields?.adress) {
        payload.adress = value.adress
      }
      if (dirtyFields?.gmt) {
        payload.gmt = { id: value.gmt.value }
      }

      if (medcomplex?.id) {
        await updateMedComplex({
          id: medcomplex?.id,
          payload: { ...payload },
        }).then(() => {
          refetch()
        })
      } else {
        const createPayload = {
          ...payload,
          modelId: payload.modelId,
          gmt: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            id: gmtOptions?.find((item: any) => item.label === 'GMT+3').value,
          },
        } as MedComplex

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        createMedComplex(createPayload).then((response: any) => {
          navigate(`/medcomplex/${response?.data?.id}/general`, {
            replace: true,
          })
        })
      }
    }
  }

  useMemo(() => {
    if (watchGeneralData[0]) {
      const model = models?.find(
        (item) => watchGeneralData[0]?.value === item.id
      )
      generalDataForm.setValue('model_type', model?.type)
    } else {
      generalDataForm.setValue('model_type', '')
    }
  }, [watchGeneralData[0]])

  const { access } = useRole('MEDCOMPLEX', ['UPDATE'])

  return (
    <Stack gap={'8px'}>
      <FormProvider {...generalDataForm}>
        <Accordion
          title={'Общие данные'}
          status={
            errorForms?.generalInfo ? (
              <Chip
                sx={styles.chip}
                variant={'outlined'}
                color={'error'}
                label={'Не заполнено'}
              />
            ) : null
          }
          defaultExpanded
          buttons={
            <Button
              onClick={generalDataForm.handleSubmit(onSubmit)}
              variant={'contained'}
              type={'submit'}
              disabled={
                !generalDataForm
                  .getValues([
                    'model',
                    'bluetooth_serial_number',
                    'ownerCompany',
                    'clientCompanies',
                    'adress',
                  ])
                  .every((e) => (Array.isArray(e) ? e.length : Boolean(e)))
              }
            >
              {medcomplex?.id ? 'Сохранить' : 'Создать'}
            </Button>
          }
        >
          <TableContainer>
            <Table sx={styles.table}>
              <TableBody>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Модель *
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <Autocomplete
                        id={''}
                        size="small"
                        name={'model'}
                        options={modelOptions || []}
                        placeholder={'Выберите модель медкомплекса'}
                      />
                    ) : (
                      medcomplex?.model.name ?? ''
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Тип *
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <Input
                        fullWidth
                        placeholder="Устанавливается автоматически"
                        sx={{
                          input: {
                            paddingTop: '7px',
                            '&.MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: 'black',
                            },
                          },
                        }}
                        disabled
                        name="model_type"
                      />
                    ) : (
                      medcomplex?.model.type ?? ''
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Часовой пояс *
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <Autocomplete
                        id={''}
                        size="small"
                        name={'gmt'}
                        options={gmtOptions || []}
                        placeholder={'Выберите часовой пояс'}
                      />
                    ) : medcomplex?.gmt ? (
                      `GMT${medcomplex?.gmt.type ?? ''}${
                        moment(medcomplex?.gmt?.value, 'hh:mm').format('h') ??
                        ''
                      }`
                    ) : (
                      'GMT+3'
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Серийный номер прибора *
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <Input
                        fullWidth
                        placeholder="Введите серийный номер"
                        sx={{
                          input: {
                            paddingTop: '7px',
                          },
                        }}
                        name="bluetooth_serial_number"
                      />
                    ) : (
                      medcomplex?.bluetooth_serial_number ?? ''
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Дата производства *
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <DateTimePicker
                        format={'DD.MM.YYYY'}
                        views={['year', 'month', 'day']}
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                            hiddenLabel: true,
                          },
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            textTransform: 'lowercase',
                          },
                        }}
                        disableFuture={true}
                        name="date_creation"
                      />
                    ) : medcomplex?.date_creation ? (
                      dayjs(medcomplex?.date_creation).format('DD.MM.YYYY')
                    ) : (
                      ''
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Гарантийный срок
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <DateTimePicker
                        format={'DD.MM.YYYY'}
                        views={['year', 'month', 'day']}
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                            hiddenLabel: true,
                          },
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            textTransform: 'lowercase',
                          },
                        }}
                        //disableFuture={true}
                        name="guarantee_period"
                      />
                    ) : medcomplex?.guarantee_period ? (
                      dayjs(medcomplex?.guarantee_period).format('DD.MM.YYYY')
                    ) : (
                      ''
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Версия приложения
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <Input
                        fullWidth
                        placeholder="Неизвестно"
                        sx={{
                          input: {
                            paddingTop: '7px',
                            '&.MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: 'black',
                            },
                          },
                        }}
                        disabled
                        name="appVersion"
                      />
                    ) : (
                      medcomplex?.appVersion ?? 'Неизвестно'
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Версия базового блока
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <Input
                        fullWidth
                        placeholder="Неизвестно"
                        sx={{
                          input: {
                            paddingTop: '7px',
                            '&.MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: 'black',
                            },
                          },
                        }}
                        disabled
                        name="coreDeviceVersion"
                      />
                    ) : (
                      medcomplex?.coreDeviceVersion ?? 'Неизвестно'
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Версия трубки
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <Input
                        fullWidth
                        placeholder="Неизвестно"
                        sx={{
                          input: {
                            paddingTop: '7px',
                            '&.MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: 'black',
                            },
                          },
                        }}
                        disabled
                        name="auxDeviceVersion"
                      />
                    ) : (
                      medcomplex?.auxDeviceVersion ?? 'Неизвестно'
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Отправлено в НИИАТ
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <Input
                        fullWidth
                        placeholder="Отправлено в НИИАТ"
                        sx={{
                          input: {
                            paddingTop: '7px',
                            '&.MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: 'black',
                            },
                          },
                        }}
                        disabled
                        name="isSendToNiiat"
                      />
                    ) : medcomplex?.isSendToNiiat ? (
                      'Да'
                    ) : (
                      'Нет'
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Собственник *
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <Autocomplete
                        id={''}
                        size="small"
                        name={'ownerCompany'}
                        options={ownerOptions || []}
                        placeholder={'Назначьте собственника'}
                        onInputChange={(_, data) => {
                          ownerAutocomplateHandle(data)
                        }}
                      />
                    ) : (
                      medcomplex?.ownerCompany?.short_title
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Клиент *
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <Autocomplete
                        id={''}
                        size="small"
                        name={'clientCompanies'}
                        options={clientOptions || []}
                        placeholder={'Назначьте клиента'}
                        onInputChange={(_, data) => {
                          clientAutocomplateHandle(data)
                        }}
                        multiple
                      />
                    ) : (
                      <Stack direction={'column'} gap={'6px'}>
                        {// eslint-disable-next-line @typescript-eslint/no-explicit-any
                        medcomplex?.clientCompanies?.map((item: any) => (
                          <Box key={item.id}>{item?.short_title}</Box>
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={'30%'} sx={{ verticalAlign: 'middle' }}>
                    Адрес клиента *
                  </TableCell>
                  <TableCell>
                    {access ? (
                      <Input
                        fullWidth
                        placeholder="Введите адрес"
                        sx={{
                          input: {
                            paddingTop: '7px',
                            '&.MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: 'black',
                            },
                          },
                        }}
                        name="adress"
                      />
                    ) : (
                      medcomplex?.adress ?? ''
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Accordion>
      </FormProvider>
    </Stack>
  )
}
